'use client';

import React, { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import './video-slider.css';

import { extractComponentFromChunk } from '@/ui/utils/tiles';
import Container from '@/ui/components/shared/Container';
import { resolveVideoSource } from '@/utils/video-source';
import {
  getVideoAspectRatio,
  getVideoMaxWidth,
} from '@/ui/components/shared/video-player/video-sizing';
import { VideoSliderSlide } from './video-slider-slide';
import { VideoSliderTile } from './video-slider-types';

export interface VideoSliderProps {
  tile: VideoSliderTile;
  trackingPrefix?: string;
}

const SLIDER_BREAKPOINTS = {
  640: { slidesPerView: 2.6, spaceBetween: 20 },
  1024: { slidesPerView: 4, spaceBetween: 24 },
  1240: { slidesPerView: 5, spaceBetween: 24 },
};

type VideoSliderChunk = VideoSliderTile['videos']['chunks'][number];

function getSourceUrl(chunk: VideoSliderChunk): string | undefined {
  const video = extractComponentFromChunk(chunk, 'video', 'VideoContent');
  const videoUrl = extractComponentFromChunk(chunk, 'video-url', 'SingleLineContent');

  return resolveVideoSource(video, videoUrl?.text)?.url;
}

/**
 * Slides are keyed by their video source so a slide keeps its player state attached to the
 * video it shows. Chunks without a playable source, or sharing one with another chunk, fall back
 * to their position so the keys stay unique.
 */
function getSlideKeys(chunks: VideoSliderChunk[]): string[] {
  const sourceUrls = chunks.map(getSourceUrl);

  return sourceUrls.map((url, index) => {
    const isUnique = url !== undefined && sourceUrls.indexOf(url) === sourceUrls.lastIndexOf(url);

    return isUnique ? url : `video-slider-slide-${index}`;
  });
}

export function VideoSlider({ tile, trackingPrefix }: Readonly<VideoSliderProps>) {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperInstance | null>(null);

  const chunks = tile.videos?.chunks ?? [];
  const aspectRatio = getVideoAspectRatio(tile['aspect-ratio']?.options?.[0]?.value, '9:16');
  const slideMaxWidth = getVideoMaxWidth(
    tile['max-width']?.number,
    tile['max-height']?.number,
    aspectRatio.ratio,
  );

  // Swiper clamps `slideTo` to its last snap and stays silent when the target is already within
  // the visible group, so the tile cannot wait for `onSlideChange` to confirm which slide was
  // meant and takes the index it asked for as the active one itself.
  const activateSlide = (index: number) => {
    swiperRef.current?.slideTo(index);
    flushSync(() => setActiveIndex(index));
  };

  // A clip that played through hands the tile on to the next slide, so the videos run as a chain.
  // Only the slide holding the active slot may do that, a chunk without a playable source renders
  // no slide at all and would end the chain on an empty one, and the last slide stops rather than
  // starting the chain over.
  const handleSlideEnded = (index: number) => {
    if (index !== activeIndex) {
      return;
    }

    const nextIndex = chunks.findIndex(
      (chunk, position) => position > index && getSourceUrl(chunk) !== undefined,
    );

    if (nextIndex === -1) {
      return;
    }

    activateSlide(nextIndex);
  };

  if (chunks.length === 0) {
    return null;
  }

  const slideKeys = getSlideKeys(chunks);

  return (
    <Container>
      <Swiper
        spaceBetween={16}
        slidesPerView={1.6}
        breakpoints={SLIDER_BREAKPOINTS}
        centerInsufficientSlides
        modules={[Pagination, Navigation]}
        navigation
        pagination={{ clickable: true }}
        className="video-slider w-full"
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        // Swiper reports outside React's event system; the newly active slide mounts its player
        // in a synchronous commit (see `useVideoPlayback`) so its lazy loader cannot race the mount.
        onSlideChange={(swiper) => flushSync(() => setActiveIndex(swiper.activeIndex))}
      >
        {chunks.map((chunk, index) => (
          <SwiperSlide key={slideKeys[index]}>
            <div
              data-testid="video-slider-slide-box"
              className="mx-auto w-full"
              style={{ maxWidth: slideMaxWidth }}
            >
              <VideoSliderSlide
                chunk={chunk}
                index={index}
                activeIndex={activeIndex}
                aspectRatioClass={aspectRatio.className}
                trackingPrefix={trackingPrefix}
                onActivate={() => activateSlide(index)}
                onEnded={() => handleSlideEnded(index)}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </Container>
  );
}
